import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { DragIndicatorOutlined } from '@mui/icons-material';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { ReactElement, ReactNode, memo, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { tokens } from '@synergy/theme-palo';

import {
    PALO_DESCRIPTION_CHEVRON_WIDTH,
    PaloDescriptionChevron,
    PaloRowDescriptionProvider,
} from './palo-position-description';
import { PALO_ROWS_PER_PAGE, PaloTableFoot, usePaloTablePage } from './palo-table-foot';
import { PaloColumnType } from './palo-column-type';
import { PaloOverflowAction, PaloOverflowMenu } from './palo-overflow-menu';
import {
    PaloPositionSelectionAllBox,
    PaloPositionSelectionBox,
    usePaloPositionSelection,
} from './palo-position-select';
import { paloColumnIsFlexible, paloColumnLayout } from './palo-column-layout';

const c = tokens.color;

/** The measure of a position column — the kind's, unless the mask said otherwise. */
/**
 * Narrower figures than in the lists, where LST-S1a still holds: a position
 * table shares its row with the name of the article, and at 1568 pixels the
 * list widths ran the invoice 211 pixels past its frame and broke names over
 * five lines (GF 19.10.2026, R-179).
 */
const POSITION_WIDTHS: Partial<Record<PaloColumnType, number>> = {
    quantity: 126,
    price: 96,
    amount: 104,
    percent: 64,
    classification: 130,
};

/** The floor of the article name in a position table; it takes what the figures leave. */
const POSITION_NAME_MIN_WIDTH = 180;

/**
 * A field in a cell takes the width of its column instead of setting it. Left
 * to itself an input is as wide as twenty characters, and that held every
 * figure at about 90 pixels whatever the column asked for (R-179).
 */
const FIELDS_FOLLOW_COLUMN_SX = {
    '& td .MuiFormControl-root, & td .MuiInputBase-root': { width: '100%', minWidth: 0 },
};

const positionLayout = <T,>(column: PaloPositionColumn<T>) => {
    const layout = paloColumnLayout({ type: column.type });
    const width = column.type ? POSITION_WIDTHS[column.type] : undefined;

    if (width) {
        return { ...layout, width: `${width}px`, minWidth: width };
    }

    return 'name' === column.type ? { ...layout, minWidth: POSITION_NAME_MIN_WIDTH } : layout;
};

const positionAlign = <T,>(column: PaloPositionColumn<T>): 'left' | 'right' | 'center' =>
    column.align ?? positionLayout(column).align ?? 'left';

export interface PaloPositionColumn<T> {
    key: string;
    label: ReactNode;
    /**
     * What the column holds. It brings the width and the alignment with it,
     * so a quantity is the same width in a position table as it is in a list
     * (LST-S1/S1a/S17, #2221). The two below override it where a mask has a
     * reason.
     */
    type?: PaloColumnType;
    /** Figures belong on the right, and so does their heading. */
    align?: 'left' | 'right' | 'center';
    width?: number | string;
    /** For a heading that has to stand somewhere else than over the whole cell. */
    headSx?: Record<string, unknown>;
    /**
     * `index` is the row's place in the whole document, not on the page.
     *
     * A mask whose rows differ too much for one renderer — an invoice has a
     * product row, a surcharge row and a free text that runs across the
     * table — hands in `renderRow` instead and leaves this out.
     */
    render?: (item: T, index: number) => ReactNode;
}

export interface PaloPositionTableProps<T> {
    items: T[];
    /**
     * Memoise these. The rows are only redrawn when something about them
     * changed, and a fresh column array on every render defeats that.
     */
    columns: PaloPositionColumn<T>[];
    getRowKey: (item: T, index: number) => string | number;
    readonly?: boolean;
    /** Carries a position to another place; the number is the handle. */
    onMove?: (from: number, to: number) => void;
    /** The entries of the row's own menu. */
    rowActions?: (item: T, index: number) => PaloOverflowAction[];
    onDeleteMany?: (indices: number[]) => void;
    onDuplicateMany?: (indices: number[]) => void;
    /**
     * Draws the cells of one row itself, in place of the columns' own
     * renderers: for masks whose rows are not all the same shape. It returns
     * the data cells only — the number, the box and the menu are the table's.
     */
    renderRow?: (item: T, index: number) => ReactNode;
    /** Sits under the table: adding a row, adding several. */
    toolbar?: ReactNode;
    /**
     * What a row counts towards the two figures under the table. Both left
     * out, nothing is summed up.
     */
    quantityOf?: (item: T) => number | undefined;
    amountOf?: (item: T) => number | undefined;
    /** For the sum under the table. */
    currency?: string;
    /**
     * What stands where the rows would be. A table of column headings over
     * nothing reads as broken rather than as empty (LST-13), so there is a
     * wording even where a mask hands none in.
     */
    emptyLabel?: ReactNode;
    rowsPerPage?: number;
    /**
     * Whether a row has a description to show. Given, each number that has
     * one gets a ▸ that opens it; the mask draws the text itself with
     * PaloPositionDescription under the article's name, and puts
     * PaloDescriptionModeToggle into the heading of its section (#2257).
     */
    hasDescription?: (item: T) => boolean;
    /**
     * A row that holds up a save: the blank first row of a document that
     * needs a complete position (#2308). It is drawn in red.
     */
    isRowFaulty?: (item: T, index: number) => boolean;
}

/**
 * The position table every document shares.
 *
 * Six masks carried six tables — a virtual one, two of MUI's own, one of the
 * house's and one that was no table at all — so every change had to be made
 * six times and drifted apart in between. This one owns what they have in
 * common: picking rows out, carrying one to another place, paging, the row
 * menu, and the quiet look of a grid rather than a form. What a document has
 * of its own arrives as columns (#2208).
 *
 * It draws a page of rows plainly rather than virtualising them. Fifty rows
 * are nothing to a browser, while a virtual table costs a measuring pass on
 * every keystroke and has to be told its height — the mask should feel quick,
 * which is as much a part of "leichtgewichtig" as the thin rows are.
 */
export function PaloPositionTable<T>({
    items,
    columns,
    getRowKey,
    readonly = false,
    onMove,
    rowActions,
    onDeleteMany,
    onDuplicateMany,
    renderRow,
    toolbar,
    quantityOf,
    amountOf,
    currency,
    emptyLabel = 'Noch keine Position',
    rowsPerPage = PALO_ROWS_PER_PAGE,
    hasDescription,
    isRowFaulty,
}: PaloPositionTableProps<T>): ReactElement {
    const selection = usePaloPositionSelection();
    const paging = usePaloTablePage(items.length, rowsPerPage);
    const [dragFrom, setDragFrom] = useState<number | null>(null);
    /** Where each pinned column stands while the rest scrolls past it. */
    const [pinned, setPinned] = useState<number[]>([]);
    const head = useRef<HTMLTableRowElement | null>(null);

    /*
     * How many columns stay, and where each of them stands.
     *
     * Up to and including the first column that holds a name: in a document
     * mask that is the number and the article, in the supplier's offer the
     * number, the "Anbieten" box and the article. Pinning the first two
     * blindly left the supplier scrolling the article away and looking at
     * prices that belonged to nothing (#2207 O-2).
     *
     * Measured rather than assumed: the first cell carries the selection box
     * and the grip in an editable table and neither in a readonly one.
     */
    const flexible = columns.findIndex(column => !column.width && paloColumnIsFlexible({ type: column.type }));
    const pinnedCount = (flexible < 0 ? 0 : flexible + 1) + 1;

    useLayoutEffect(() => {
        const measure = (): void => {
            const cells = Array.from(head.current?.children ?? []).slice(0, pinnedCount);
            let left = 0;

            setPinned(
                cells.map(cell => {
                    const at = left;
                    left += cell.getBoundingClientRect().width;

                    return at;
                })
            );
        };

        measure();
        window.addEventListener('resize', measure);

        return () => window.removeEventListener('resize', measure);
    }, [readonly, onMove, pinnedCount, items.length]);
    const [dragOver, setDragOver] = useState<number | null>(null);
    // The drop reads the ref: a drag that ends before React has drawn again
    // would otherwise find nothing to carry.
    const carried = useRef<number | null>(null);

    const pageRows = items.slice(paging.offset, paging.offset + paging.limit);
    const pageIndexes = useMemo(
        () => pageRows.map((_, index) => paging.offset + index),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [paging.offset, pageRows.length]
    );

    const keyOf = useCallback((index: number) => getRowKey(items[index], index), [items, getRowKey]);

    const endDrag = (): void => {
        carried.current = null;
        setDragFrom(null);
        setDragOver(null);
    };

    const handleDrop = (to: number): void => {
        const from = carried.current;

        if (null !== from && from !== to && onMove) {
            onMove(from, to);
        }

        endDrag();
    };

    const table = (
        <Box>
            {!readonly && selection.selected.length > 0 && (
                <PaloPositionSelectionBar
                    count={selection.selected.length}
                    onDuplicate={() => {
                        onDuplicateMany?.(selection.sorted());
                        selection.clear();
                    }}
                    onDelete={() => {
                        onDeleteMany?.(selection.sorted());
                        selection.clear();
                    }}
                    onCancel={selection.clear}
                />
            )}

            {/* A frame around the rows, so the table reads as one part of the
                form rather than a band running through it — the way ERPNext
                sets its grid into the mask (#2208). */}
            <Box
                sx={{
                    border: `1px solid ${c.line}`,
                    borderRadius: `${tokens.radius.small}px`,
                    backgroundColor: c.surface,
                    /*
                     * The columns carry a measure of their own now, and the
                     * seven of an invoice need about 1.230 pixels — more than
                     * a notebook leaves beside the right rail. Clipped, the
                     * price and the position value were simply unreachable;
                     * the table scrolls sideways in its own frame instead,
                     * which is what the rule asks for (#2207 R-142).
                     */
                    overflowX: 'auto',
                    overflowY: 'hidden',
                }}
            >
                <Table
                    size="small"
                    sx={mergeRules(
                        TABLE_SX,
                        pinnedColumnsSx(pinned),
                        columnAlignmentSx(columns),
                        // An opened description makes its row tall; the
                        // figures stay level with the article's name.
                        hasDescription ? DESCRIPTION_SX : {},
                        FIELDS_FOLLOW_COLUMN_SX
                    )}
                >
                    <TableHead>
                        <TableRow ref={head}>
                            <TableCell sx={{ width: '1%', whiteSpace: 'nowrap' }}>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '1px' }}>
                                    {!readonly && (
                                        <PaloPositionSelectionAllBox selection={selection} indices={pageIndexes} />
                                    )}
                                    {/* The grip in front of each number has no
                                        counterpart up here, so the heading sat
                                        a grip's width left of the numbers it
                                        names (LST-S2). */}
                                    {onMove && !readonly && <Box sx={{ width: 15, flexShrink: 0 }} />}
                                    {hasDescription && (
                                        <Box sx={{ width: PALO_DESCRIPTION_CHEVRON_WIDTH, flexShrink: 0 }} />
                                    )}
                                    <FormattedMessage id="app.table.column.positionNumber" defaultMessage="Pos." />
                                </Box>
                            </TableCell>
                            {columns.map(column => (
                                <TableCell
                                    key={column.key}
                                    sx={{
                                        width: column.width ?? positionLayout(column).width,
                                        // A width the mask sets is meant: the kind's
                                        // minimum held the order's prices at 150 px
                                        // and pushed its table past the mask (#2259).
                                        minWidth: column.width ? undefined : positionLayout(column).minWidth,
                                        textAlign: positionAlign(column),
                                    }}
                                >
                                    {/* On the label, not on the cell: the
                                        table's own rule for `th` outweighs a
                                        cell's padding. */}
                                    {column.headSx ? <Box sx={column.headSx}>{column.label}</Box> : column.label}
                                </TableCell>
                            ))}
                            {rowActions && <TableCell sx={{ width: 38 }} />}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pageRows.map((item, rowIndex) => {
                            const index = paging.offset + rowIndex;

                            return (
                                <PaloPositionRow
                                    key={getRowKey(item, index)}
                                    item={item}
                                    index={index}
                                    columns={columns}
                                    readonly={readonly}
                                    selected={selection.isSelected(index)}
                                    onSelect={selection.toggle}
                                    carried={dragFrom === index}
                                    hovered={dragOver === index}
                                    dragUpwards={null !== dragFrom && dragFrom > index}
                                    actions={rowActions?.(item, index)}
                                    renderRow={renderRow}
                                    draggable={Boolean(onMove)}
                                    hasDescription={hasDescription}
                                    faulty={!!isRowFaulty?.(item, index)}
                                    onDragStart={value => {
                                        carried.current = value;
                                        setDragFrom(value);
                                    }}
                                    onDragEnter={setDragOver}
                                    onDragEnd={endDrag}
                                    onDrop={handleDrop}
                                />
                            );
                        })}
                        {0 === items.length && emptyLabel && (
                            <TableRow>
                                <TableCell
                                    colSpan={columns.length + (rowActions ? 2 : 1)}
                                    sx={{ textAlign: 'center', color: c.ink3, padding: '18px 0' }}
                                >
                                    {emptyLabel}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Box>

            {/* Under the table, where ERPNext keeps them: one adds a row at
                the end of the list, so the button belongs at the end of it.
                What the list comes to stands at its other end (#2208). */}
            {(toolbar || quantityOf || amountOf) && (
                <Box sx={{ mt: '8px', display: 'flex', alignItems: 'flex-end', gap: '16px' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>{toolbar}</Box>
                    <PaloPositionSummary
                        items={items}
                        quantityOf={quantityOf}
                        amountOf={amountOf}
                        currency={currency}
                    />
                </Box>
            )}

            {paging.paged && (
                <PaloTableFoot
                    count={items.length}
                    page={paging.page}
                    limit={paging.limit}
                    maxResults={null}
                    onPageChange={paging.setPage}
                    onLimitChange={paging.setLimit}
                />
            )}
        </Box>
    );

    return hasDescription ? <PaloRowDescriptionProvider keyOf={keyOf}>{table}</PaloRowDescriptionProvider> : table;
}

/**
 * A grid, not a form: no box around an untouched value, a border on hover, the
 * accent on focus and red kept for an error.
 */
/**
 * Heading and values of a column share an edge, even where the mask draws its
 * own cells: a column of figures read as two columns while the numbers stood
 * right and their heading left (#2208).
 */
/**
 * Two rule maps over one table, merged per selector.
 *
 * Spreading them would not merge but replace: both name cells by position, so
 * `& td:nth-of-type(2)` written by the alignment dropped the ground the pinned
 * column had put there, and the rows scrolling past showed through the
 * "Anbieten" box (#2239). Only the second level needs merging — each value is
 * a flat set of declarations.
 */
const mergeRules = (...maps: Record<string, unknown>[]): Record<string, unknown> =>
    maps.reduce<Record<string, unknown>>((all, map) => {
        Object.entries(map).forEach(([selector, declarations]) => {
            const before = all[selector];

            all[selector] =
                before && 'object' === typeof before && 'object' === typeof declarations
                    ? { ...(before as object), ...(declarations as object) }
                    : declarations;
        });

        return all;
    }, {});

const columnAlignmentSx = <T,>(columns: PaloPositionColumn<T>[]) =>
    columns.reduce<Record<string, unknown>>((rules, column, index) => {
        const align = positionAlign(column);

        if ('left' !== align) {
            // The first cell of a row is the number, so the columns start at two.
            rules[`& td:nth-of-type(${index + 2})`] = { textAlign: align };
            // A field does not inherit the cell's alignment, and it keeps
            // padding of its own: the figures stood at the left edge of their
            // fields while the heading stood at the right edge of the column,
            // which is what made the column read as two. The cell's own padding
            // is what holds both away from the edge.
            rules[`& td:nth-of-type(${index + 2}) .MuiOutlinedInput-input`] = {
                textAlign: align,
                paddingRight: 0,
            };
        }

        return rules;
    }, {});

/**
 * The two columns that stay while the rest scrolls sideways.
 *
 * A table wider than the form scrolls in its frame, and the first thing to
 * leave was the article — price, discount and position value then stood
 * without saying what they belonged to. The number and the article hold
 * their place instead, so only the figures move (#2207 O-2, GF 13.10.2026).
 *
 * Written as rules on the table rather than on the cells: the masks whose
 * rows differ too much draw their own cells, and they are pinned too.
 */
const pinnedColumnsSx = (offsets: number[]) =>
    offsets.reduce<Record<string, unknown>>((rules, left, index) => {
        const nth = index + 1;
        const last = index === offsets.length - 1;

        rules[`& th:nth-of-type(${nth}), & td:nth-of-type(${nth})`] = {
            position: 'sticky',
            left: `${left}px`,
            zIndex: 2,
            // The edge of what stands still; without it the scrolling figures
            // slide under the article and read as part of it.
            ...(last ? { borderRight: `1px solid ${c.line}` } : {}),
        };
        // A pinned cell needs a ground of its own, or the rows it covers show
        // through it.
        rules[`& td:nth-of-type(${nth})`] = { backgroundColor: c.surface };
        rules[`& th:nth-of-type(${nth})`] = { backgroundColor: c.surfaceAlt, zIndex: 3 };

        return rules;
    }, {});

/**
 * An opened description makes its row tall: the figures stay level with the
 * article's name rather than sliding to the middle of the row. Text stands
 * as low as the text inside the fields beside it, the fields themselves keep
 * their place (#2257).
 */
const DESCRIPTION_SX = {
    '& td': { verticalAlign: 'top', paddingTop: '7px' },
    '& td .MuiInputBase-root': { marginTop: '-4px' },
    '& td .MuiCheckbox-root, & td .MuiButtonBase-root.MuiIconButton-root': { marginTop: '-4px' },
} as const;

const TABLE_SX = {
    tableLayout: 'auto',
    width: '100%',
    '& th': {
        backgroundColor: c.surfaceAlt,
        fontSize: '11px',
        fontWeight: 600,
        letterSpacing: '.04em',
        textTransform: 'uppercase',
        color: c.ink3,
        borderBottom: `1px solid ${c.lineHard}`,
        padding: '5px 8px',
        whiteSpace: 'nowrap',
    },
    '& td': {
        fontSize: '12.5px',
        color: c.ink,
        borderBottom: `1px solid ${c.line}`,
        padding: '3px 8px',
        // The theme gives every table row a list's 38 px. A position row holds
        // its fields in 26 px, and the masks are to show ten of them without
        // scrolling (#2250, #2251).
        height: 32,
    },
    // The frame draws the last line.
    '& tbody tr:last-of-type td': { borderBottom: 'none' },
    // Doubled so it outweighs the theme's own border on the same class.
    '&& .MuiOutlinedInput-root': { backgroundColor: 'transparent' },
    '&& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
    '&& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: c.line },
    '&& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: c.accent,
        borderWidth: '1px',
    },
    '&& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: c.crit },
    '&& .MuiOutlinedInput-input': { paddingTop: '3px', paddingBottom: '3px' },
    // A choice in a row (the counter account) is as low as a typed figure;
    // the theme's 29 px minimum for an autocomplete beside form fields made
    // every position row 4 px higher (#2250).
    '&& .MuiAutocomplete-inputRoot': { paddingTop: 0, paddingBottom: 0, minHeight: 0 },
    '&& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': { paddingTop: '3px', paddingBottom: '3px' },
    // The search in a row that is still empty has to be visible without being
    // focused first: everything else in the table is a value that stands there
    // already, this one is an invitation to type (#2208).
    '&& .palo-row-search .MuiOutlinedInput-notchedOutline': { borderColor: c.line },
    '&& .palo-row-search .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: c.accent },
    '&& .palo-row-search .MuiOutlinedInput-input::placeholder': { opacity: 1, color: c.ink3 },
    // A label over a field costs a line in every row; the column says it once.
    '& .MuiInputLabel-root': { display: 'none' },
    '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
} as const;

/**
 * How much the list comes to, under it.
 *
 * ERPNext writes "Total Quantity" and "Total" straight under its grid, and the
 * mask's own bar at the top carries the sum of the whole document — the two
 * are not the same thing once there are surcharges or shipping on it (#2208).
 */
function PaloPositionSummary<T>({
    items,
    quantityOf,
    amountOf,
    currency,
}: {
    items: T[];
    quantityOf?: (item: T) => number | undefined;
    amountOf?: (item: T) => number | undefined;
    currency?: string;
}): ReactElement | null {
    const sum = (of?: (item: T) => number | undefined): number | undefined =>
        of ? items.reduce((total, item) => total + (of(item) ?? 0), 0) : undefined;

    const quantity = sum(quantityOf);
    const amount = sum(amountOf);

    if (undefined === quantity && undefined === amount) {
        return null;
    }

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'baseline',
                gap: '18px',
                fontSize: '12px',
                color: c.ink3,
                whiteSpace: 'nowrap',
                paddingBottom: '4px',
            }}
        >
            {undefined !== quantity && (
                <Box>
                    <FormattedMessage id="app.positions.totalQuantity" defaultMessage="Gesamtmenge" />{' '}
                    <Box component="span" sx={{ color: c.ink, fontWeight: 600 }}>
                        <FormattedNumber value={quantity} maximumFractionDigits={2} />
                    </Box>
                </Box>
            )}
            {undefined !== amount && (
                <Box>
                    <FormattedMessage id="app.positions.totalAmount" defaultMessage="Summe" />{' '}
                    <Box component="span" sx={{ color: c.ink, fontWeight: 600 }}>
                        {currency ? (
                            <FormattedNumber value={amount} style="currency" currency={currency} />
                        ) : (
                            <FormattedNumber value={amount} maximumFractionDigits={2} />
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );
}

interface PaloPositionRowProps<T> {
    item: T;
    index: number;
    columns: PaloPositionColumn<T>[];
    readonly: boolean;
    selected: boolean;
    onSelect: (index: number) => void;
    carried: boolean;
    hovered: boolean;
    dragUpwards: boolean;
    actions?: PaloOverflowAction[];
    renderRow?: (item: T, index: number) => ReactNode;
    draggable: boolean;
    hasDescription?: (item: T) => boolean;
    faulty: boolean;
    onDragStart: (index: number) => void;
    onDragEnter: (index: number) => void;
    onDragEnd: () => void;
    onDrop: (index: number) => void;
}

/**
 * Drawn again only when something about this row changed.
 *
 * A keystroke in one row used to redraw all fifty, which is what made a long
 * document feel slow. Formik hands out a new object for the row that changed
 * and keeps the others, so comparing the item is enough.
 */
const PaloPositionRowInner = <T,>({
    item,
    index,
    columns,
    readonly,
    selected,
    onSelect,
    carried,
    hovered,
    dragUpwards,
    actions,
    renderRow,
    draggable,
    hasDescription,
    faulty,
    onDragStart,
    onDragEnter,
    onDragEnd,
    onDrop,
}: PaloPositionRowProps<T>): ReactElement => (
    <TableRow
        onDragEnter={draggable && !readonly ? () => onDragEnter(index) : undefined}
        onDragOver={
            draggable && !readonly
                ? event => {
                      event.preventDefault();
                      event.dataTransfer.dropEffect = 'move';
                      onDragEnter(index);
                  }
                : undefined
        }
        onDrop={
            draggable && !readonly
                ? event => {
                      event.preventDefault();
                      onDrop(index);
                  }
                : undefined
        }
        sx={{
            ...(carried ? { opacity: 0.35, backgroundColor: c.neutralSoft } : {}),
            ...(hovered && !carried ? { boxShadow: `inset 0 ${dragUpwards ? '3px' : '-3px'} 0 ${c.accent}` } : {}),
            '&:hover .palo-grip': { opacity: 1 },
            ...(faulty
                ? {
                      '& > td': {
                          // Over the pinned columns too, which paint their own.
                          backgroundColor: `${c.critSoft} !important`,
                          borderTop: `1px solid ${c.crit}`,
                          borderBottom: `1px solid ${c.crit}`,
                      },
                      '& > td:first-of-type': { borderLeft: `1px solid ${c.crit}` },
                      '& > td:last-of-type': { borderRight: `1px solid ${c.crit}` },
                  }
                : {}),
        }}
    >
        <TableCell
            draggable={draggable && !readonly}
            title={draggable && !readonly ? 'Zum Verschieben ziehen' : undefined}
            onDragStart={
                draggable && !readonly
                    ? event => {
                          // Firefox starts no drag without something on the transfer.
                          event.dataTransfer.setData('text/plain', String(index));
                          event.dataTransfer.effectAllowed = 'move';
                          onDragStart(index);
                      }
                    : undefined
            }
            onDragEnd={draggable && !readonly ? onDragEnd : undefined}
            sx={{
                whiteSpace: 'nowrap',
                cursor: draggable && !readonly ? 'grab' : undefined,
                userSelect: 'none',
                '&:active': draggable && !readonly ? { cursor: 'grabbing' } : undefined,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '1px' }}>
                {!readonly && (
                    <PaloPositionSelectionBox selected={selected} index={index} onChange={() => onSelect(index)} />
                )}
                {draggable && !readonly && (
                    <DragIndicatorOutlined
                        className="palo-grip"
                        sx={{ fontSize: 15, color: c.ink3, opacity: 0.45, transition: 'opacity .1s' }}
                    />
                )}
                {hasDescription && <PaloDescriptionChevron index={index} has={hasDescription(item)} />}
                <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {index + 1}
                </Box>
            </Box>
        </TableCell>
        {renderRow
            ? renderRow(item, index)
            : columns.map(column => (
                  <TableCell key={column.key} sx={{ textAlign: positionAlign(column) }}>
                      {column.render?.(item, index)}
                  </TableCell>
              ))}
        {actions && (
            <TableCell sx={{ width: 38 }}>{actions.length > 0 && <PaloOverflowMenu actions={actions} />}</TableCell>
        )}
    </TableRow>
);

const PaloPositionRow = memo(PaloPositionRowInner) as typeof PaloPositionRowInner;

const PaloPositionSelectionBar = ({
    count,
    onDuplicate,
    onDelete,
    onCancel,
}: {
    count: number;
    onDuplicate: () => void;
    onDelete: () => void;
    onCancel: () => void;
}): ReactElement => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '4px 11px',
            mb: '6px',
            borderRadius: `${tokens.radius.small}px`,
            backgroundColor: c.accentSoft,
            border: `1px solid ${c.accentLine}`,
            fontSize: '12.5px',
        }}
    >
        <FormattedMessage
            id="app.positions.selected"
            values={{ value: count }}
            defaultMessage="{value, plural, one {# Position ausgewählt} other {# Positionen ausgewählt}}"
        />
        <Box sx={{ marginLeft: 'auto', display: 'flex', gap: '14px' }}>
            <Box role="button" onClick={onDuplicate} sx={BAR_ACTION_SX}>
                <FormattedMessage id="app.common.duplicate" defaultMessage="Duplizieren" />
            </Box>
            <Box role="button" onClick={onDelete} sx={{ ...BAR_ACTION_SX, color: c.crit }}>
                <FormattedMessage id="app.common.delete" defaultMessage="Löschen" />
            </Box>
            <Box role="button" onClick={onCancel} sx={{ ...BAR_ACTION_SX, color: c.ink3 }}>
                <FormattedMessage id="app.common.cancel" defaultMessage="Abbrechen" />
            </Box>
        </Box>
    </Box>
);

const BAR_ACTION_SX = {
    cursor: 'pointer',
    color: c.accentHi,
    '&:hover': { textDecoration: 'underline' },
} as const;
